import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Download, Database, Clock, Check, FileJson, FileSpreadsheet,
    Upload, RotateCcw, HardDrive, CloudUpload, FolderSync,
    AlertTriangle, Loader2, ExternalLink, Plug
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Backup() {
    const [exportingJson, setExportingJson] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [driveRestoreDialogOpen, setDriveRestoreDialogOpen] = useState(false);
    const [driveRestoreFileId, setDriveRestoreFileId] = useState(null);
    const [driveRestoreFileName, setDriveRestoreFileName] = useState('');
    const [restoringFromDrive, setRestoringFromDrive] = useState(false);

    const [driveConnected, setDriveConnected] = useState(false);
    const [driveInfo, setDriveInfo] = useState({ email: null, folder_link: null });
    const [driveLoading, setDriveLoading] = useState(true);
    const [driveBackups, setDriveBackups] = useState([]);
    const [driveUploading, setDriveUploading] = useState(false);
    const [backupLogs, setBackupLogs] = useState([]);

    const fileInputRef = useRef(null);

    useEffect(() => {
        checkDriveStatus();
        fetchBackupLogs();

        const params = new URLSearchParams(window.location.search);
        if (params.get('drive_connected') === 'true') {
            toast.success('Google Drive basariyla baglandi!');
            window.history.replaceState({}, '', window.location.pathname);
            checkDriveStatus();
        }
        if (params.get('drive_error') === 'true') {
            toast.error('Google Drive baglantisi basarisiz');
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const getToken = () => localStorage.getItem('token');

    const checkDriveStatus = async () => {
        try {
            setDriveLoading(true);
            const res = await fetch(`${API_URL}/api/drive/status`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDriveConnected(data.connected);
                setDriveInfo({ email: data.email || null, folder_link: data.folder_link || null });
                if (data.connected) fetchDriveBackups();
            }
        } catch (e) {
            console.error('Drive status error:', e);
        } finally {
            setDriveLoading(false);
        }
    };

    const fetchDriveBackups = async () => {
        try {
            const res = await fetch(`${API_URL}/api/drive/list-backups`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                setDriveBackups(await res.json());
            }
        } catch (e) {
            console.error('Drive backups error:', e);
        }
    };

    const fetchBackupLogs = async () => {
        try {
            const res = await fetch(`${API_URL}/api/backup/logs`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) setBackupLogs(await res.json());
        } catch (e) {
            console.error('Backup logs error:', e);
        }
    };

    const handleConnectDrive = async () => {
        try {
            const res = await fetch(`${API_URL}/api/drive/connect`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const data = await res.json();
                window.location.href = data.authorization_url;
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Drive baglantisi baslatılamadi');
            }
        } catch (e) {
            toast.error('Drive baglantisi basarisiz');
        }
    };

    const downloadFile = async (url, filename, setLoading) => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}${url}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
                toast.success('Dosya indirildi');
            } else if (res.status === 403) {
                toast.error('Admin yetkisi gerekli');
            } else {
                toast.error('Indirme basarisiz');
            }
        } catch (e) {
            toast.error('Indirme sirasinda hata');
        } finally {
            setLoading(false);
        }
    };

    const handleExportJson = () => {
        downloadFile('/api/backup/export', `yedek_${new Date().toISOString().split('T')[0]}.json`, setExportingJson);
    };

    const handleExportExcel = () => {
        downloadFile('/api/backup/export-excel', `yedek_${new Date().toISOString().split('T')[0]}.xlsx`, setExportingExcel);
    };

    const handleRestore = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.endsWith('.json')) {
            toast.error('Sadece JSON dosyasi yuklenebilir');
            return;
        }
        setRestoreDialogOpen(true);
    };

    const confirmRestore = async () => {
        const file = fileInputRef.current?.files[0];
        if (!file) return;
        try {
            setRestoring(true);
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_URL}/api/backup/restore?confirm=DELETE_ALL_AND_RESTORE`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}` },
                body: formData
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Geri yukleme tamamlandi! ${data.restored.length} koleksiyon yuklendi`);
                fetchBackupLogs();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Geri yukleme basarisiz');
            }
        } catch (e) {
            toast.error('Geri yukleme sirasinda hata');
        } finally {
            setRestoring(false);
            setRestoreDialogOpen(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDriveUpload = async () => {
        try {
            setDriveUploading(true);
            const res = await fetch(`${API_URL}/api/drive/upload-backup`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                toast.success('Yedek Google Drive\'a yuklendi!');
                fetchDriveBackups();
                fetchBackupLogs();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Drive yukleme basarisiz');
            }
        } catch (e) {
            toast.error('Drive yukleme hatasi');
        } finally {
            setDriveUploading(false);
        }
    };

    const handleDriveRestore = (fileId, fileName) => {
        setDriveRestoreFileId(fileId);
        setDriveRestoreFileName(fileName);
        setDriveRestoreDialogOpen(true);
    };

    const confirmDriveRestore = async () => {
        if (!driveRestoreFileId) return;
        try {
            setRestoringFromDrive(true);
            const res = await fetch(`${API_URL}/api/drive/restore-from-drive/${driveRestoreFileId}?confirm=DELETE_ALL_AND_RESTORE`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Drive'dan geri yukleme tamamlandi! ${data.restored.length} koleksiyon`);
                fetchBackupLogs();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Geri yukleme basarisiz');
            }
        } catch (e) {
            toast.error('Geri yukleme hatasi');
        } finally {
            setRestoringFromDrive(false);
            setDriveRestoreDialogOpen(false);
            setDriveRestoreFileId(null);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '-';
        const kb = parseInt(bytes) / 1024;
        if (kb > 1024) return `${(kb / 1024).toFixed(1)} MB`;
        return `${kb.toFixed(0)} KB`;
    };

    return (
        <div className="space-y-6" data-testid="backup-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Yedekleme & Geri Yukleme</h1>
                <p className="text-slate-500 text-sm mt-1">Veritabani yedeklerini indirin, Google Drive'a yukleyin veya geri yukleyin</p>
            </div>

            {/* Download Section */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {/* JSON Export */}
                <Card className="border-t-4 border-t-blue-500">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <FileJson className="w-5 h-5 text-blue-500" />
                            JSON Yedek
                        </CardTitle>
                        <CardDescription>Tam veritabani yedegi (geri yuklenebilir)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            onClick={handleExportJson}
                            disabled={exportingJson}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            data-testid="export-json-btn"
                        >
                            {exportingJson ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            {exportingJson ? 'Hazirlaniyor...' : 'JSON Indir'}
                        </Button>
                    </CardContent>
                </Card>

                {/* Excel Export */}
                <Card className="border-t-4 border-t-green-500">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-green-500" />
                            Excel Yedek
                        </CardTitle>
                        <CardDescription>Tablo formatinda veri yedegi</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            onClick={handleExportExcel}
                            disabled={exportingExcel}
                            className="w-full bg-green-600 hover:bg-green-700"
                            data-testid="export-excel-btn"
                        >
                            {exportingExcel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            {exportingExcel ? 'Hazirlaniyor...' : 'Excel Indir'}
                        </Button>
                    </CardContent>
                </Card>

                {/* Restore from file */}
                <Card className="border-t-4 border-t-amber-500">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <RotateCcw className="w-5 h-5 text-amber-500" />
                            Geri Yukle
                        </CardTitle>
                        <CardDescription>JSON dosyasindan veritabanini geri yukle</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            onChange={handleRestore}
                            className="hidden"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            variant="outline"
                            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid="restore-file-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            JSON Dosyasi Sec
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* Google Drive Section */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <HardDrive className="w-5 h-5 text-indigo-500" />
                                Google Drive Yedekleme
                            </CardTitle>
                            <CardDescription>
                                Yedekleriniz otomatik olarak "GrandKaptanHotel" klasorune kaydedilir
                            </CardDescription>
                        </div>
                        {driveLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        ) : driveConnected ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                                <Check className="w-3.5 h-3.5 mr-1" /> Bagli
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="text-slate-500">Bagli Degil</Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <AutoBackupScheduler />
                    {!driveConnected && !driveLoading && (
                        <div className="bg-indigo-50 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-indigo-800">Google Drive hesabinizi baglayin</p>
                                <p className="text-xs text-indigo-600 mt-1">
                                    Yedekleriniz Google Drive'daki "GrandKaptanHotel" klasorune otomatik kaydedilecek
                                </p>
                            </div>
                            <Button
                                onClick={handleConnectDrive}
                                className="bg-indigo-600 hover:bg-indigo-700"
                                data-testid="connect-drive-btn"
                            >
                                <Plug className="w-4 h-4 mr-2" />
                                Google Drive Bagla
                            </Button>
                        </div>
                    )}

                    {driveConnected && (
                        <>
                            {/* Bağlı hesap bilgisi + Klasör linki */}
                            {(driveInfo.email || driveInfo.folder_link) && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="drive-account-info">
                                    <div className="text-sm">
                                        <span className="text-slate-500">Bağlı hesap:</span>{' '}
                                        <span className="font-semibold text-emerald-800" data-testid="drive-bound-email">
                                            {driveInfo.email || 'Bilinmiyor'}
                                        </span>
                                    </div>
                                    {driveInfo.folder_link && (
                                        <a
                                            href={driveInfo.folder_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                                            data-testid="drive-folder-link"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            GrandKaptanHotel klasörünü aç
                                        </a>
                                    )}
                                </div>
                            )}

                            <Button
                                onClick={handleDriveUpload}
                                disabled={driveUploading}
                                className="bg-indigo-600 hover:bg-indigo-700"
                                data-testid="drive-upload-btn"
                            >
                                {driveUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-2" />}
                                {driveUploading ? 'Yukleniyor...' : 'Drive\'a Yedekle'}
                            </Button>

                            {/* Drive Backups List */}
                            {driveBackups.length > 0 && (
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-slate-50 px-4 py-2 border-b">
                                        <h4 className="text-sm font-medium text-slate-600 flex items-center gap-2">
                                            <FolderSync className="w-4 h-4" />
                                            Drive Yedekleri ({driveBackups.length})
                                        </h4>
                                    </div>
                                    <div className="divide-y max-h-64 overflow-y-auto">
                                        {driveBackups.map((file) => (
                                            <div key={file.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                                                <div>
                                                    <p className="text-sm font-medium text-slate-700">{file.name}</p>
                                                    <p className="text-xs text-slate-400">
                                                        {new Date(file.createdTime).toLocaleString('tr-TR')} | {formatBytes(file.size)}
                                                    </p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleDriveRestore(file.id, file.name)}
                                                    data-testid={`drive-restore-${file.id}`}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                                    Geri Yukle
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Backup Logs */}
            {backupLogs.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Clock className="w-5 h-5 text-slate-400" />
                            Yedekleme Gecmisi
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y max-h-48 overflow-y-auto">
                            {backupLogs.map((log, idx) => (
                                <div key={idx} className="flex items-center gap-3 px-4 py-2.5">
                                    {log.action === 'restore' && <RotateCcw className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                                    {log.action === 'drive_upload' && <CloudUpload className="w-4 h-4 text-indigo-500 flex-shrink-0" />}
                                    {log.action === 'drive_connected' && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
                                    {log.action === 'restore_from_drive' && <FolderSync className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-slate-700 truncate">
                                            {log.action === 'restore' && 'Dosyadan geri yukleme'}
                                            {log.action === 'drive_upload' && `Drive'a yuklendi: ${log.file_name || ''}`}
                                            {log.action === 'drive_connected' && 'Google Drive baglandi'}
                                            {log.action === 'restore_from_drive' && 'Drive\'dan geri yukleme'}
                                        </p>
                                    </div>
                                    <span className="text-xs text-slate-400 flex-shrink-0">
                                        {log.restored_at ? new Date(log.restored_at).toLocaleString('tr-TR') : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Restore Confirmation Dialog */}
            <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <AlertTriangle className="w-5 h-5" />
                            Geri Yukleme Onayi
                        </DialogTitle>
                        <DialogDescription>
                            Bu islem mevcut tum verilerin uzerine yazacaktir. Devam etmek istediginize emin misiniz?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        Mevcut veriler silinecek ve yedek dosyasindan geri yuklenecektir. Bu islem geri alinamaz.
                    </div>
                    <div className="flex gap-3 justify-end">
                        <Button variant="outline" onClick={() => { setRestoreDialogOpen(false); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                            Iptal
                        </Button>
                        <Button onClick={confirmRestore} disabled={restoring} className="bg-amber-600 hover:bg-amber-700">
                            {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                            {restoring ? 'Yukleniyor...' : 'Geri Yukle'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Drive Restore Confirmation Dialog */}
            <Dialog open={driveRestoreDialogOpen} onOpenChange={setDriveRestoreDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <AlertTriangle className="w-5 h-5" />
                            Drive'dan Geri Yukleme
                        </DialogTitle>
                        <DialogDescription>
                            "{driveRestoreFileName}" dosyasindan geri yukleme yapilacak.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        Mevcut veriler silinecek ve secilen yedekten geri yuklenecektir. Bu islem geri alinamaz.
                    </div>
                    <div className="flex gap-3 justify-end">
                        <Button variant="outline" onClick={() => setDriveRestoreDialogOpen(false)}>
                            Iptal
                        </Button>
                        <Button onClick={confirmDriveRestore} disabled={restoringFromDrive} className="bg-amber-600 hover:bg-amber-700">
                            {restoringFromDrive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                            {restoringFromDrive ? 'Yukleniyor...' : 'Geri Yukle'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ===================== AUTO BACKUP SCHEDULER COMPONENT =====================
function AutoBackupScheduler() {
    const [settings, setSettings] = React.useState({ enabled: false, frequency: 'daily', hour: 3, target: 'drive', last_run: null, last_status: null });
    const [saving, setSaving] = React.useState(false);
    const [running, setRunning] = React.useState(false);

    React.useEffect(() => {
        api.get('/backup/schedule').then(r => setSettings(prev => ({ ...prev, ...r.data })));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/backup/schedule', {
                enabled: settings.enabled, frequency: settings.frequency,
                hour: parseInt(settings.hour, 10), target: settings.target,
            });
            toast.success('Otomatik yedek ayarı kaydedildi');
        } catch (e) {
            toast.error('Kaydetme başarısız');
        } finally { setSaving(false); }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await api.post('/backup/schedule/run-now');
            toast.success(`Yedek alındı: ${r.data.drive_uploaded ? 'Drive\'a yüklendi ✓' : 'Lokale yazıldı'}`);
            const fresh = await api.get('/backup/schedule');
            setSettings(prev => ({ ...prev, ...fresh.data }));
        } catch (e) {
            toast.error('Yedek başarısız');
        } finally { setRunning(false); }
    };

    return (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200" data-testid="auto-backup-scheduler">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h4 className="font-semibold text-emerald-900">🤖 Otomatik Yedek Planlayıcısı</h4>
                    <p className="text-xs text-emerald-700">Belirlenen saatte tüm DB'yi yedekler + Drive'a yükler</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={settings.enabled}
                        onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                        className="w-4 h-4 accent-emerald-600" data-testid="auto-backup-enabled" />
                    <span className="text-sm font-medium">{settings.enabled ? 'Açık' : 'Kapalı'}</span>
                </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                <div>
                    <label className="text-xs text-slate-600">Sıklık</label>
                    <select value={settings.frequency} onChange={(e) => setSettings({ ...settings, frequency: e.target.value })}
                        className="w-full h-9 border rounded-md px-2 text-sm bg-white" data-testid="auto-backup-frequency">
                        <option value="daily">Günlük</option>
                        <option value="weekly">Haftalık (Pazartesi)</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs text-slate-600">Saat (TR)</label>
                    <input type="number" min="0" max="23" value={settings.hour}
                        onChange={(e) => setSettings({ ...settings, hour: e.target.value })}
                        className="w-full h-9 border rounded-md px-2 text-sm" data-testid="auto-backup-hour" />
                </div>
                <div>
                    <label className="text-xs text-slate-600">Hedef</label>
                    <select value={settings.target} onChange={(e) => setSettings({ ...settings, target: e.target.value })}
                        className="w-full h-9 border rounded-md px-2 text-sm bg-white" data-testid="auto-backup-target">
                        <option value="drive">Google Drive</option>
                        <option value="local">Sadece sunucu (geçici)</option>
                    </select>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="auto-backup-save">
                    {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="auto-backup-run-now">
                    {running ? 'Çalışıyor...' : 'Şimdi Yedekle'}
                </Button>
                {settings.last_run && (
                    <span className="text-xs text-slate-600">
                        Son çalışma: <strong>{new Date(settings.last_run).toLocaleString('tr-TR')}</strong>
                        {settings.last_status?.drive_uploaded && ' · Drive ✓'}
                        {settings.last_status?.error && ` · HATA: ${settings.last_status.error.substring(0, 50)}`}
                    </span>
                )}
            </div>
        </div>
    );
}

export default Backup;
